import { ArrayField, AutocompleteArrayInput, ChipField, CreateButton, Datagrid, DateField, Filter, FunctionField, List, ReferenceArrayInput, ReferenceField, ReferenceInput, SearchInput, SelectArrayInput, SelectInput, TextField, TopToolbar, useListContext } from 'react-admin';
import { CheckChainUsesModule, ModuleKey } from '../../components';
import ListPagination from '../../components/ListPagination';
import Permission from '../../components/Permission';
import { PERMISSIONS, frequencyChoices } from '../../constants';
import { getChainId } from '../../lib';
import { GiftProductType, GiftRuleType, TagType } from '../../models';
import CloneGiftButton from './CloneGiftButton';
import ToggleGiftButton from './ToggleGiftButton';
import { filterToQuery } from '../../utils';
import { GiftExpandableRow } from './components/list';
import ManualLinkButton from '../../manual/components/ManualLinkButton';
import FeatureIntro from '../../components/FeatureIntro';

const PlaceField = () => {
    const { data } = useListContext();
    const values = Object.values(data);

    return (
        <>
            {values.slice(0, 4).map(place => (
                <ReferenceField source="placeId" reference="places" record={place} >
                    <ChipField sx={{ margin: '2px' }} source="name" />
                </ReferenceField>
            )
            )}
            <>{values.length > 4 ? <> mais {values.length - 4} locais{values.length - 4 > 1 ? 's' : ''}</> : ''}</>
        </>);
};

/** Vigência é sobre as datas do brinde; Status é sobre o `isEnabled`. São coisas diferentes. */
const validityChoices = [
    { id: 'current', name: 'Vigentes' },
    { id: 'scheduled', name: 'Agendados' },
    { id: 'expired', name: 'Encerrados' },
];

const filterInputSx = { minWidth: 200 };

// Brinde de campanha de recuperação não é cadastrado nem editado aqui: a campanha é dona
// dele. Fora do filtro padrão para não poluir a lista do Clube com um brinde por nível.
const originChoices = [
    { id: 'club', name: 'Clube de Vantagens' },
    { id: 'recovery', name: 'Campanha de recuperação' },
    { id: 'all', name: 'Todos' },
];

// Fora do GiftList de propósito: componente declarado dentro do render vira um
// tipo novo a cada re-render e o React remonta a árvore inteira do filtro — o
// SearchInput perderia o foco a cada tecla.
const ListFilters = (props) => {
    const { filterValues } = useListContext();
    return (
        <Filter {...props}>
            <SearchInput
                source="search"
                placeholder="Nome, código ou código de divulgação"
                sx={{ minWidth: 280 }}
                alwaysOn
            />
            <SelectInput
                label="Status"
                source="isEnabled"
                choices={[{ label: 'Todos', value: 'all' }, { label: 'Ativos', value: true }, { label: 'Inativos', value: false }]}
                optionText="label"
                optionValue="value"
                alwaysOn
            />
            {
                Permission({
                    permission: PERMISSIONS.ADMIN, children:
                        <ReferenceInput
                            label="Rede"
                            reference="chains"
                            source="chainId"
                            style={{ minWidth: 220 }}
                            defaultValue={getChainId()}
                            sort={{ field: 'name', order: 'ASC' }}
                            perPage={9999}
                        >
                            <SelectInput label="Rede" optionText="name" />
                        </ReferenceInput>
                })
            }
            <ReferenceArrayInput
                reference="places"
                source="placeIds"
                sort={{ field: 'name', order: 'ASC' }}
                perPage={9999}
                filter={filterValues.chainId ? { chainId: filterValues.chainId } : {}}
                alwaysOn
            >
                <AutocompleteArrayInput
                    label="Locais de troca"
                    optionText="name"
                    filterToQuery={filterToQuery}
                    sx={{ minWidth: 220, '& .MuiAutocomplete-inputRoot .MuiAutocomplete-input': { minWidth: 50 } }}
                />
            </ReferenceArrayInput>
            {/* Múltipla escolha porque os tipos são exclusivos POR REGRA: a pergunta
                real quase sempre é "abastecimento OU compra". Traz o brinde que tem
                pelo menos uma regra de algum dos tipos marcados. */}
            <SelectArrayInput
                label="Tipo de regra"
                source="ruleTypes"
                choices={GiftRuleType.giftRuleTypeChoices}
                sx={{ minWidth: 240 }}
                alwaysOn
            />
            <SelectInput
                label="Vigência"
                source="validity"
                choices={validityChoices}
                sx={filterInputSx}
            />
            <SelectInput
                label="Frequência"
                source="frequency"
                choices={frequencyChoices}
                sx={filterInputSx}
            />
            <SelectArrayInput
                label="Tipo de recompensa"
                source="productTypes"
                choices={GiftProductType.giftProductTypeChoices}
                sx={filterInputSx}
            />
            <SelectInput
                label="Origem"
                source="origin"
                choices={originChoices}
                sx={filterInputSx}
            />
            {
                Permission({
                    permission: PERMISSIONS.TAG, children:
                        <ReferenceArrayInput
                            label="Etiqueta"
                            source="tagIds"
                            reference="chains/chainId/tag"
                            perPage={9999}
                            sort={{ field: 'name', order: 'ASC' }}
                            filter={{ type: TagType.client, chainId: filterValues.chainId }}
                        >
                            <SelectArrayInput label="Etiqueta" optionText="name" sx={filterInputSx} />
                        </ReferenceArrayInput>
                })
            }
            {/* Saiu do alwaysOn: o filtro de tipo de regra cobre a maior parte do
                caso de uso dele e a barra já estava cheia. */}
            <ReferenceArrayInput
                label="Combustíveis"
                reference="fuels"
                source="fuelIds"
                sort={{ field: "name", order: "ASC" }}
            >
                <SelectArrayInput label="Combustíveis" optionText="name" sx={filterInputSx} />
            </ReferenceArrayInput>
        </Filter>
    );
};

const GiftList = (props) => {

    const chainId = getChainId();

    const filterDefaultValues: any = { isEnabled: true, origin: 'club' };
    if (chainId) {
        filterDefaultValues.chainId = chainId;
    }

    return (
        <CheckChainUsesModule module={ModuleKey.GIFT}>
            <Permission permission={PERMISSIONS.GIFT}>
                <List
                    {...props}
                    title="Clube de Vantagens"
                    sort={{ field: 'createdAt', order: 'DESC' }}
                    filterDefaultValues={filterDefaultValues}
                    filters={<ListFilters />}
                    exporter={false}
                    pagination={<ListPagination />}
                    perPage={25}
                    actions={
                        <TopToolbar>
                            {/* O actions customizado substitui o ListActions padrão, que é quem
                                renderiza o botão "Adicionar filtro". Sem esta linha, todo filtro
                                que não for alwaysOn fica inalcançável. */}
                            <ListFilters context="button" />
                            {Permission({ permission: PERMISSIONS.ADMIN, children: <FeatureIntro id="manual-clube-vantagens" title="Manual do Clube de Vantagens" description="Acesse o manual completo sobre o Clube de Vantagens para entender regras, produtos, conquistas e muito mais."><ManualLinkButton slug="clube-de-vantagens" label="Manual do Clube" /></FeatureIntro> })}
                            <CreateButton />
                        </TopToolbar>
                    }
                >
                    <Datagrid
                        bulkActionButtons={false}
                        rowClick="show"
                        expand={<GiftExpandableRow />}
                    >
                        <TextField source="identify" label="Código" />
                        <TextField source="name" label="Nome" emptyText="--" />
                        <DateField source="startAt" label="Data de início" showTime textAlign="center" emptyText="--" />
                        <DateField source="endAt" label="Data de fim" showTime textAlign="center" emptyText="--" />
                        <FunctionField
                            render={record => record && record.frequency ? frequencyChoices.find(dt => dt.id === record.frequency)?.name : '--'}
                            label="Frequência"
                            textAlign="center"
                        />
                        <ArrayField label="Locais de troca" source="placeGifts" sortable={false}>
                            <PlaceField />
                        </ArrayField>
                        <FunctionField
                            render={record => record && record.isEnabled ? "Ativo" : "Inativo"}
                            label="Status"
                            textAlign="center"
                        />
                        <CloneGiftButton />
                        <ToggleGiftButton />
                    </Datagrid>
                </List>
            </Permission>
        </CheckChainUsesModule>
    );
};

export default GiftList;
